import { csrfToken } from "@/api/client";

export type ServerEvent = { type: string; data: unknown; ts: string };
export type Status = "connecting" | "open" | "reconnecting" | "closed";
type Handler = (data: unknown) => void;

// Realtime is an accelerator (ADR-0017): anything missed while disconnected is re-fetched over
// REST when the socket comes back, which is what the "reconnected" pseudo-event triggers.
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private statusHandlers = new Set<(s: Status) => void>();
  private rooms = new Map<string, number>();
  private attempt = 0;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private everOpened = false;
  status: Status = "connecting";

  constructor(private url: string) {}

  start() {
    this.stopped = false;
    this.connect();
    document.addEventListener("visibilitychange", this.onVisible);
  }

  stop() {
    this.stopped = true;
    document.removeEventListener("visibilitychange", this.onVisible);
    if (this.timer) clearTimeout(this.timer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close(1000);
    this.ws = null;
    this.setStatus("closed");
  }

  // A tab coming back from the background reconnects at once instead of waiting out the backoff.
  private onVisible = () => {
    if (document.visibilityState === "visible" && this.status !== "open" && !this.stopped) {
      if (this.timer) clearTimeout(this.timer);
      this.attempt = 0;
      this.connect();
    }
  };

  private setStatus(s: Status) {
    this.status = s;
    this.statusHandlers.forEach((h) => h(s));
  }

  private connect() {
    this.setStatus(this.everOpened ? "reconnecting" : "connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: "auth", csrf: csrfToken() }));
    ws.onmessage = (e) => {
      let ev: ServerEvent;
      try {
        ev = JSON.parse(e.data as string);
      } catch {
        return;
      }
      if (ev.type === "ready") {
        const reconnect = this.everOpened;
        this.everOpened = true;
        this.attempt = 0;
        this.setStatus("open");
        this.rooms.forEach((_, id) => this.send({ type: "subscribe", conversation_id: id }));
        if (reconnect) this.emit("reconnected", null);
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = setInterval(() => this.send({ type: "ping" }), 20000);
        return;
      }
      this.emit(ev.type, ev.data);
    };
    ws.onclose = (e) => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (this.stopped) return;
      if (e.code === 1008) {
        // Policy violation: the session is gone. The API layer will route to sign-in.
        this.emit("session.revoked", null);
        this.setStatus("closed");
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    this.setStatus("reconnecting");
    const base = Math.min(30000, 1000 * 2 ** this.attempt);
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempt++;
    this.timer = setTimeout(() => this.connect(), delay);
  }

  private send(frame: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  private emit(type: string, data: unknown) {
    this.handlers.get(type)?.forEach((h) => h(data));
    this.handlers.get("*")?.forEach((h) => h({ type, data }));
  }

  on(type: string, h: Handler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(h);
    return () => this.handlers.get(type)?.delete(h);
  }

  onStatus(h: (s: Status) => void): () => void {
    this.statusHandlers.add(h);
    return () => this.statusHandlers.delete(h);
  }

  // Rooms are reference-counted so two components watching the same conversation don't
  // unsubscribe each other.
  join(conversationId: string): () => void {
    const n = this.rooms.get(conversationId) ?? 0;
    this.rooms.set(conversationId, n + 1);
    if (n === 0) this.send({ type: "subscribe", conversation_id: conversationId });
    return () => {
      const left = (this.rooms.get(conversationId) ?? 1) - 1;
      if (left <= 0) {
        this.rooms.delete(conversationId);
        this.send({ type: "unsubscribe", conversation_id: conversationId });
      } else this.rooms.set(conversationId, left);
    };
  }

  typing(conversationId: string, state: "start" | "stop") {
    this.send({ type: "typing", conversation_id: conversationId, state });
  }
}

export function socketURL(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/admin/v1/ws`;
}
